import { Box, Container, Flex } from '@moonpig/launchpad-components'
import { Breadcrumbs } from '@moonpig/web-core-nav'
import React, { FC } from 'react'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import {
  AddedToBasketEvent,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { ProductGridWithTracking } from '../ProductGridWithTracking'
import { FixedBottomBar } from '../FixedBottomBar'
import { Breadcrumb } from '../../types'
import { generateBreadcrumbsClickGAEvent } from '../../analytics/GAEvents'
import { getSortByOptions } from '../../utils'
import { useFindLocaleText } from '../../text-localisation'
import { FiltersMenu } from '../FiltersMenu'
import { InternalUserGalleryInfo } from '../InternalUserGalleryInfo'
import type { SearchContext } from '../../store/slices/shared'
import { FiltersBar } from '../FiltersBar/FiltersBar'
import { ShoppingList } from '../ShoppingList'
import { useSearchStore } from '../../store/SearchStore'
import { useExperiment } from '@moonpig/web-core-eppo/browser'

const StyledBox = styled(Box)`
  ${s({
    bgcolor: 'colorBackground01',
    pb: 6,
  })}
`

const StyledFlex = styled(Flex)`
  display: flex;
  width: 100%;
`

const StyledHeader = styled(Container)`
  position: relative;
  overflow: visible;
  z-index: 501;
  ${s({
    paddingTop: { xs: 6, md: 8 },
    marginX: 'auto',
    pl: { xs: 6, xl: 10 },
  })}
`

const StyledGridContainer = styled(Container)`
  position: relative;
  width: 100%;
`

export type SearchResultsProps = {
  renderHeader: () => React.ReactNode
  searchContext: SearchContext
  totalCount: number
  products: ProductTileProduct[]
  zeroResults: boolean
  experiments?: { [key: string]: string } | undefined
  suggestions: ProductTileProduct[]
  breadcrumbs: Breadcrumb[]
  isInternalUser: boolean
  addingToBasket: boolean
  onAddedToBasket: (e: AddedToBasketEvent) => Promise<void>
  searchTitle: string
  pageType: PageTypeEnum
  customerId?: string
  metaTitle: string
  totalClickRankDocumentCount: number
}

export const SearchResults: FC<SearchResultsProps> = ({
  renderHeader,
  searchContext,
  totalCount,
  products,
  zeroResults,
  experiments,
  suggestions,
  breadcrumbs,
  isInternalUser,
  onAddedToBasket,
  searchTitle,
  pageType,
  customerId,
  metaTitle,
  totalClickRankDocumentCount,
}) => {
  const localiseText = useFindLocaleText()
  const { searchTerm, promotionId, departments } = searchContext
  const multiBuyEnabled = useSearchStore(store => store.multiBuyEnabled)
  const isMultiBuySupported = useSearchStore(
    store => store.results.isMultiBuySupported,
  )
  const useMultiBuy =
    useExperiment('christmas-multi-buy', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const isCards =
    getParentDepartment(departments[0]) === DepartmentsEnum.ALL_CARDS

  const sortByOptions = getSortByOptions({
    isCards,
    localiseText,
  })

  const showFilterControls = !zeroResults

  const showShoppingList = isMultiBuySupported && multiBuyEnabled && useMultiBuy

  return (
    <StyledBox>
      {isInternalUser && (
        <InternalUserGalleryInfo
          productCount={totalCount}
          searchTerm={searchTerm}
          filters={[]}
          promotionId={promotionId}
          departments={departments}
          totalClickRankDocumentCount={totalClickRankDocumentCount}
        />
      )}

      <Box>
        <StyledHeader limitWidth>
          {!!breadcrumbs.length && (
            <Box
              onClick={() => {
                trackGAEvent(generateBreadcrumbsClickGAEvent(breadcrumbs))
              }}
              data-testid="breadcrumb-header"
            >
              <Breadcrumbs items={breadcrumbs} />
            </Box>
          )}
          {renderHeader()}
        </StyledHeader>
      </Box>
      <FiltersMenu sortOptions={sortByOptions} />
      {showFilterControls && (
        <FiltersBar
          departments={departments}
          withNbaFilters={pageType !== PageTypeEnum.COLLECTION}
        />
      )}
      <StyledFlex flexDirection="row">
        <StyledGridContainer limitWidth padding={0}>
          <ProductGridWithTracking
            totalCount={zeroResults ? 0 : totalCount}
            pageTitle={searchTitle}
            metaTitle={metaTitle}
            searchContext={searchContext}
            products={
              (products?.length && products) ||
              (suggestions?.length && suggestions) ||
              []
            }
            onAddedToBasket={onAddedToBasket}
            isInternalUser={isInternalUser}
            presetFacets={[]}
            experiments={experiments}
            pageType={pageType}
            customerId={customerId}
          />
        </StyledGridContainer>
      </StyledFlex>

      <FixedBottomBar pageTitle={searchTitle} />
      {showShoppingList && <ShoppingList />}
    </StyledBox>
  )
}
